// src/components/web-booking/SpecialDateDialog.js
// 特殊日期 / 節日例外 新增·編輯（Plan 094 Phase 2）
// 公司層級、純網頁端 — 無語音音檔欄位（語音音檔走 voice-flow ConstraintEditDialog）。
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Button,
  TextField, Box, Typography, MenuItem, FormControlLabel, Checkbox, Stack, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const TYPES = [
  { value: 'blackout', label: '完全不接受', color: '#c62828', bg: '#fef0f0', hint: '整天或指定時段完全不接受訂位（最高優先）' },
  { value: 'closed', label: '當日店休', color: '#e65100', bg: '#fff8e1', hint: '當日店休；可整天或指定關閉時段' },
  { value: 'limited_hours', label: '部分時段不開放', color: '#1565c0', bg: '#e3f2fd', hint: '只開放某個時段（窗外灰掉）；一定要填開放時段' }
];

const EMPTY = {
  name: '', constraintType: 'blackout', startDate: '', endDate: '',
  allDay: true, startTime: '17:00', endTime: '21:00', recurring: '', messageText: ''
};

const SpecialDateDialog = ({ open, initial, onClose, onSave, busy }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm')); // 手機全螢幕表單，取消/儲存釘底
  const [f, setF] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setF({
        name: initial.name || '',
        constraintType: initial.constraintType || 'blackout',
        startDate: initial.startDate || '',
        endDate: initial.endDate || initial.startDate || '',
        allDay: initial.allDay !== false,
        startTime: (initial.startTime || '17:00').slice(0, 5),
        endTime: (initial.endTime || '21:00').slice(0, 5),
        recurring: initial.recurring || '',
        messageText: initial.messageText || ''
      });
    } else {
      setF(EMPTY);
    }
  }, [open, initial]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isLimited = f.constraintType === 'limited_hours';
  const needTime = isLimited || !f.allDay;

  const submit = () => {
    onSave({
      name: f.name || '(未命名)',
      constraintType: f.constraintType,
      startDate: f.startDate,
      endDate: f.endDate || f.startDate,
      allDay: isLimited ? false : f.allDay,
      startTime: needTime ? f.startTime : null,
      endTime: needTime ? f.endTime : null,
      recurring: f.recurring || null,
      messageText: f.messageText || '所選日期暫不開放訂位'
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 800 }}>{initial ? '編輯特殊日期' : '新增特殊日期'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="名稱" size="small" fullWidth value={f.name}
            onChange={(e) => set('name', e.target.value)} placeholder="例：母親節禁訂" />

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#555', mb: 1 }}>類型</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {TYPES.map((t) => {
                const on = f.constraintType === t.value;
                return (
                  <Box key={t.value} onClick={() => set('constraintType', t.value)}
                    sx={{
                      flex: 1, textAlign: 'center', py: 1, borderRadius: 2, cursor: 'pointer',
                      fontSize: 13, fontWeight: 600, border: '1px solid',
                      ...(on
                        ? { bgcolor: t.bg, borderColor: t.color, color: t.color }
                        : { bgcolor: '#fff', borderColor: '#e0e0e0', color: '#888' })
                    }}>
                    {t.label}
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ fontSize: 12, color: '#999', mt: 0.75 }}>
              {TYPES.find((t) => t.value === f.constraintType)?.hint}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="開始日期" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
              value={f.startDate} onChange={(e) => set('startDate', e.target.value)} />
            <TextField label="結束日期" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
              value={f.endDate} onChange={(e) => set('endDate', e.target.value)} />
          </Box>

          {!isLimited && (
            <FormControlLabel
              control={<Checkbox checked={f.allDay} onChange={(e) => set('allDay', e.target.checked)} />}
              label="整天（不指定時段）" />
          )}

          {needTime && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label={isLimited ? '開放時段開始' : '時段開始'} type="time" size="small" fullWidth
                InputLabelProps={{ shrink: true }} value={f.startTime} onChange={(e) => set('startTime', e.target.value)} />
              <TextField label={isLimited ? '開放時段結束' : '時段結束'} type="time" size="small" fullWidth
                InputLabelProps={{ shrink: true }} value={f.endTime} onChange={(e) => set('endTime', e.target.value)} />
            </Box>
          )}

          <TextField label="重複" select size="small" fullWidth value={f.recurring}
            onChange={(e) => set('recurring', e.target.value)}>
            <MenuItem value="">不重複（指定日期）</MenuItem>
            <MenuItem value="yearly_by_date">每年同日</MenuItem>
          </TextField>

          <TextField label="顧客訊息（網路與電話都會用到）" size="small" fullWidth multiline value={f.messageText}
            onChange={(e) => set('messageText', e.target.value)}
            placeholder="例：母親節當天恕不接受訂位，敬請見諒" />

          {/* 儲存/取消移到顧客訊息下方（短表單也看得到、不用捲到底找）*/}
          <Stack direction="row" spacing={1.5} sx={{ pt: 0.5 }}>
            <Button onClick={onClose} disabled={busy} variant="outlined" fullWidth sx={{ py: 1.1 }}>取消</Button>
            <Button onClick={submit} variant="contained" disabled={busy || !f.startDate} fullWidth sx={{ py: 1.1 }}>儲存</Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default SpecialDateDialog;
